/**
 * MasterDataClaimConditionFormDialog — Create/Edit `claim_condition`.
 * 1:1 port of Access `frmMdQryClaimConditionAdd`. Compact Sales Contract style.
 *
 * Single-column (Access Top order):
 *   Organization → Weighing Date → Weight Claim → Quality Claims → Others.
 * `organization` là PK text: required, disable khi edit. VBA: organization trống
 * → "Please type Organization!"; trùng (create) → "Organization is existed!"
 * (BE raise → snackbar). Weighing Date/Weight Claim/Quality Claims/Others là số
 * nguyên (Access Format='0').
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdClaimConditionMutation,
  useUpdateMdClaimConditionMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataClaimCondition } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataClaimCondition | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  organization: string
  weighing_date: string
  weight_claim: string
  quality_claims: string
  others: string
}

const EMPTY: FormState = {
  organization: '',
  weighing_date: '',
  weight_claim: '',
  quality_claims: '',
  others: '',
}

export default function MasterDataClaimConditionFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createClaimCondition, { isLoading: creating }] = useCreateMdClaimConditionMutation()
  const [updateClaimCondition, { isLoading: updating }] = useUpdateMdClaimConditionMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    if (isEdit && initialValue) {
      setForm({
        organization: initialValue.organization ?? '',
        weighing_date: initialValue.weighing_date != null ? String(initialValue.weighing_date) : '',
        weight_claim: initialValue.weight_claim != null ? String(initialValue.weight_claim) : '',
        quality_claims: initialValue.quality_claims != null ? String(initialValue.quality_claims) : '',
        others: initialValue.others != null ? String(initialValue.others) : '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const orgEmpty = !form.organization.trim()
  const canSave = !orgEmpty && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const toInt = (s: string): number | null => {
    const t = s.trim()
    if (!t) return null
    const n = parseInt(t, 10)
    return Number.isNaN(n) ? null : n
  }

  const buildBody = () => ({
    weighing_date: toInt(form.weighing_date),
    weight_claim: toInt(form.weight_claim),
    quality_claims: toInt(form.quality_claims),
    others: toInt(form.others),
  })

  const handleSave = async () => {
    if (orgEmpty) {
      setShowErrors(true)
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateClaimCondition({ code: initialValue.organization, body: buildBody() }).unwrap()
        snack.success('Claim condition updated')
      } else {
        await createClaimCondition({ organization: form.organization.trim(), ...buildBody() }).unwrap()
        snack.success('Claim condition created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Claim Condition' : 'New Claim Condition'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.organization}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Organization"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && orgEmpty}
              value={form.organization}
              onChange={(e) => set('organization', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1 }}
              helperText={isEdit ? 'Organization cannot be changed after creation.' : undefined}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Weighing Date"
              size="small"
              fullWidth
              type="number"
              value={form.weighing_date}
              onChange={(e) => set('weighing_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2, inputMode: 'numeric', step: 1, style: { textAlign: 'right' } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Weight Claim"
              size="small"
              fullWidth
              type="number"
              value={form.weight_claim}
              onChange={(e) => set('weight_claim', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3, inputMode: 'numeric', step: 1, style: { textAlign: 'right' } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Quality Claims"
              size="small"
              fullWidth
              type="number"
              value={form.quality_claims}
              onChange={(e) => set('quality_claims', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 4, inputMode: 'numeric', step: 1, style: { textAlign: 'right' } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Others"
              size="small"
              fullWidth
              type="number"
              value={form.others}
              onChange={(e) => set('others', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 5, inputMode: 'numeric', step: 1, style: { textAlign: 'right' } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
